import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';
import { getOperatorBasePath, isDistributorRole } from '../utils/operator';

const CommercialOrdersPage = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<string>('');
  const basePath = getOperatorBasePath(auth.user?.role);
  const isDistributor = isDistributorRole(auth.user?.role);

  useEffect(() => {
    const load = async () => {
      const res = await api.orders.getOrders();
      if (res.success) setOrders(res.data || []);
      setLoading(false);
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    let list = orders;
    if (q) {
      const qq = q.toLowerCase();
      list = list.filter((o) => {
        const name = o.customer?.name || o.customerInfo?.name || '';
        return [o._id, name, o.status].filter(Boolean).some((v: string) => String(v).toLowerCase().includes(qq));
      });
    }
    if (status) list = list.filter(o => o.status === status);
    return list;
  }, [orders, q, status]);

  const exportCSV = () => {
    const rows = filtered.map((o: any) => ({
      id: o._id,
      cliente: o.customer?.name || o.customerInfo?.name || '',
      destinatario: o.dropShipping?.recipient?.name || '',
      estado: o.status,
      total: o.totalAmount || 0,
      fecha: o.createdAt
    }));
    const header = Object.keys(rows[0] || { id: '' }).join(',');
    const lines = rows.map(r => Object.values(r).map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const csv = [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pedidos.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{isDistributor ? 'Pedidos para cliente final' : 'Pedidos'}</h1>
        <div className="flex items-center gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar (id, cliente, estado)" className="border rounded px-3 py-2" />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded px-3 py-2">
            <option value="">Todos</option>
            <option value="pending">pending</option>
            <option value="processing">processing</option>
            <option value="shipped">shipped</option>
            <option value="delivered">delivered</option>
            <option value="cancelled">cancelled</option>
          </select>
          <button onClick={exportCSV} className="px-3 py-2 border rounded">Exportar CSV</button>
        </div>
      </div>
      {loading ? (
        <div className="animate-pulse h-6 w-32 bg-gray-200 rounded" />
      ) : (
        <div className="grid gap-3">
          {filtered.length === 0 && <p className="text-gray-500">Sin pedidos</p>}
          {filtered.map((o) => (
            <div
              key={o.id || o._id}
              className="border rounded p-3 flex items-center justify-between hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => navigate(`${basePath}/orders/${o._id}`)}
            >
              <div>
                <div className="font-medium">#{o._id?.slice?.(-6) || o._id}</div>
                <div className="text-sm text-gray-600">{o.customer?.name || o.customerInfo?.name || 'Cliente'}</div>
                {o.dropShipping?.recipient?.name && (
                  <div className="text-xs text-amber-700">
                    Enviar a: {o.dropShipping.recipient.name}
                    {o.dropShipping.notifyRecipient ? ' · email logístico' : ''}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-700">{o.totalAmount?.toFixed?.(2)}€ · {o.status}</div>
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`${basePath}/orders/${o._id}`); }}
                  className="px-3 py-1 border rounded text-sm font-medium hover:bg-gray-100"
                >
                  Ver
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommercialOrdersPage;
